/* eslint-disable security/detect-object-injection */
import {Box, Button, Collapse, Typography} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import React, {JSX, useEffect, useMemo, useRef, useState} from 'react';
import {t} from 'i18next';
import _ from 'lodash';
import ArrSingleSelectDropdown from '../../../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {ISelectItem} from '../../../../../../shared/types';
import {
  ArrSourceSelectedContainerProps,
  SelectedSource,
  SourceItem,
} from '../../../../tally/selectSourceContainer/ArrSourceSelectedContainer';
import {ArrDrawer} from '../../../../../common/arr-drawer/ArrDrawer';
import ArrTreeView from '../../../../../common/arr-tree-view/ArrTreeView';
import ArrSelectionChip from '../../../../../common/arr-selection-chip/ArrSelectionChip';
import './ArrSourceSelectedSection.scss';

// Source section ids reused across the export source-selection flow
export const ASSIGNMENT_SOURCE_ID = 'assignments';
export const ASSIGNMENT_EXPORT_SECTION_ID = 'assignmentexport';

interface ArrSourceSelectedSectionProps extends ArrSourceSelectedContainerProps {
  sourcesDropdownSize?: 'small' | 'medium' | 'large';
  multiSelectSources?: boolean; // If true, allows multiple sources to be selected one by one from dropdown
  resetSignal?: boolean;
  initialSelectedSources?: Record<string, any>;
  disableSourceSelection?: boolean;
  sortSecurity?: boolean;
}
const supportedSectionId = [
  'securitygroups',
  'reviewers',
  'custodians',
  'searches',
  'assignments',
  'folders',
  'ingestion',
  'docexplorerexport',
  'tallyexport',
  'searchtermexport',
  'advbatmgmtrptexport',
  'sessionsearchexport',
  'timelineexport',
  'doclistexport',
  'savedsearchexport',
  'assignmentexport',
];

export const getSectionMeta = (sectionId: string) => {
  switch (sectionId) {
    case 'securityGroups':
    case 'securitygroups':
      return {
        sourceName: 'SECURITY GROUPS',
        navigationSourceType: 'SECGROUP',
      };
    case 'reviewers':
      return {sourceName: 'REVIEWERS', navigationSourceType: 'REVIEWER'};
    case 'custodians':
      return {sourceName: 'CUSTODIANS', navigationSourceType: 'CUSTODIAN'};
    case 'searches':
      return {sourceName: 'SEARCHES', navigationSourceType: 'SEARCH'};
    case 'assignments':
      return {sourceName: 'ASSIGNMENTS', navigationSourceType: 'ASSIGNMENT'};
    case 'folders':
      return {sourceName: 'FOLDERS', navigationSourceType: 'FOLDER'};
    case 'docexplorerexport':
      return {sourceName: 'DOCLIST', navigationSourceType: 'DOCEXPLORER'};
    case 'tallyexport':
      return {sourceName: 'TALLY', navigationSourceType: 'TALLY'};
    case 'searchtermexport':
      return {sourceName: 'SEARCHTERM', navigationSourceType: 'SEARCHTERM'};
    case 'advbatmgmtrptexport':
      return {
        sourceName: 'ADVBATMGMTRPT',
        navigationSourceType: 'ADVBATMGMTRPT',
      };
    case 'sessionsearchexport':
      return {
        sourceName: 'SESSIONSEARCH',
        navigationSourceType: 'SESSIONSEARCH',
      };
    case 'timelineexport':
      return {
        sourceName: 'TIMELINE',
        navigationSourceType: 'TIMELINE',
      };
    case 'doclistexport':
      return {sourceName: 'DOCLIST', navigationSourceType: 'DOCLIST'};
    case 'savedsearchexport':
      return {
        sourceName: 'SEARCHES',
        navigationSourceType: 'SAVEDSEARCH',
      };
    case ASSIGNMENT_EXPORT_SECTION_ID:
      return {sourceName: 'ASSIGNMENTS', navigationSourceType: 'ASSIGNMENT'};
    default:
      return {
        sourceName: sectionId?.toUpperCase?.() ?? '',
        navigationSourceType: sectionId?.toUpperCase?.() ?? '',
      };
  }
};

const ArrSourceSelectedSection: React.FC<ArrSourceSelectedSectionProps> = ({
  sources,
  onSelectionsUpdate,
  sourcesDropdownSize = 'small',
  multiSelectSources = true,
  resetSignal,
  initialSelectedSources,
  disableSourceSelection = false,
  sortSecurity = false,
}) => {
  // Disabled while the prepopulated source is locked. Cleared once the user
  // removes the prepopulated source or resets, so they can pick a source.
  const [disabledSourceSelection, setDisableSourceSelection] = useState(
    disableSourceSelection
  );
  const [selectedSources, setSelectedSources] = useState<SelectedSource[]>([]);
  const [committedSourceType, setCommittedSourceType] =
    useState<ISelectItem | null>(null);
  const [drawerSourceType, setDrawerSourceType] = useState<ISelectItem | null>(
    null
  );
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [selectSourceKey, setSelectSourceKey] = useState<number>(0);
  const [dropdownResetKey, setDropdownResetKey] = useState<number>(0);
  const idRef = useRef<number>(1);
  const [selectedIdsBySection, setSelectedIdsBySection] = useState<
    Record<string, string[] | null>
  >({});
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [fullSelectionsBySection, setFullSelectionsBySection] = useState<
    Record<string, any[] | null>
  >({});
  const [draftSelectedIdsBySection, setDraftSelectedIdsBySection] = useState<
    Record<string, string[] | null>
  >({});
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const sourceOptions: ISelectItem[] = sources
    .filter((s) => !(Array.isArray(s.data) && s.data.length === 0))
    .map((s) => ({id: s.id, name: s.label}));

  const currentSectionId = drawerSourceType?.id?.toString?.() ?? '';

  const currentSection = useMemo<SourceItem | null>(
    () => sources.find((s) => s.id === currentSectionId) ?? null,
    [sources, currentSectionId]
  );

  const pickNodeLabel = (node: any) =>
    node?.textWithoutCount ??
    node?.text ??
    node?.securityGroupLabel ??
    node?.projectLabel ??
    node?.label ??
    node?.name ??
    node?.kayName ??
    '';

  const sortedSectionData = useMemo<any[]>(() => {
    const data = currentSection?.data ?? [];
    const isSecurityGroupSection =
      currentSectionId?.toLowerCase?.() === 'securitygroups' ||
      currentSectionId === 'securityGroups';
    if (!sortSecurity || !isSecurityGroupSection || !Array.isArray(data)) {
      return data;
    }
    const copy = [...data];
    const norm = (v: any) => (v ?? '').toString().trim().toLowerCase();
    const pick = (n: any) => norm(pickNodeLabel(n));
    const DEFAULT_NAME = 'default security group';
    copy.sort((a, b) => {
      const la = pick(a);
      const lb = pick(b);
      const aIsDefault = la === DEFAULT_NAME;
      const bIsDefault = lb === DEFAULT_NAME;
      if (aIsDefault && !bIsDefault) return -1;
      if (!aIsDefault && bIsDefault) return 1;
      return la.localeCompare(lb);
    });
    return copy;
  }, [currentSection, currentSectionId, sortSecurity]);

  useEffect(() => {
    if (resetSignal) {
      setSelectedSources([]);
      setCommittedSourceType(null);
      setDrawerSourceType(null);
      setDrawerOpen(false);
      setSelectSourceKey(0);
      setDropdownResetKey((k) => k + 1);
      setSelectedIdsBySection({});
      setDraftSelectedIdsBySection({});
      setFullSelectionsBySection({});
      // Reset clears the prepopulated source, so enable the source dropdown
      setDisableSourceSelection(false);
    }
  }, [resetSignal]);

  useEffect(() => {
    setInitialSelections();
  }, []);

  const publishSelections = (structured: Record<string, any[] | null>) => {
    onSelectionsUpdate?.({...structured});
  };

  const setInitialSelections = () => {
    if (!initialSelectedSources) return;
    const selectedSources: SelectedSource[] = [];
    const selectionIdsBySection: {[key: string]: string[]} = {};
    Object.keys(initialSelectedSources ?? {}).forEach((sectionId) => {
      if (
        !supportedSectionId.includes(sectionId.toLowerCase()) ||
        !Array.isArray(_.get(initialSelectedSources, sectionId, []))
      )
        return;

      _.get(initialSelectedSources, sectionId, []).forEach((entry: any) => {
        const sectionMeta = getSectionMeta(sectionId.toLowerCase());
        const section = sources.find((s) => s.id === sectionId);

        // For Tally report exports that is not in the source
        if (sectionMeta.navigationSourceType === 'TALLY') {
          selectedSources.push({
            id: idRef.current++,
            sectionId,
            sectionLabel: t('reports.tally.exportSectionLabel'),
            label: t('reports.tally.exportLabel'),
            nodeId: entry.source,
            hideClose: true,
          });
          const tallyIds: string[] | null = Array.isArray(entry.source)
            ? entry.source.map(String)
            : entry.source != null
              ? [String(entry.source)]
              : null;
          _.set(selectionIdsBySection, sectionId, tallyIds);
        }

        // For Doc Explorer report exports that is not in the source
        if (sectionMeta.navigationSourceType === 'DOCEXPLORER') {
          selectedSources.push({
            id: idRef.current++,
            sectionId,
            sectionLabel: t('reports.export.searchTerm.exportSectionLabel'),
            label: t('reports.export.docExplorer.exportLabel'),
            nodeId: entry.source,
            hideClose: true,
          });
          const docExplorerIds: string[] | null = Array.isArray(entry.source)
            ? entry.source.map(String)
            : entry.source != null
              ? [String(entry.source)]
              : null;
          _.set(selectionIdsBySection, sectionId, docExplorerIds);
        }

        if (sectionMeta.navigationSourceType === 'TIMELINE') {
          selectedSources.push({
            id: idRef.current++,
            sectionId,
            sectionLabel: '',
            label: t('reports.export.timeLine.exportLabel'),
            nodeId: entry.source,
            hideClose: true,
          });
          const timeLineIds: string[] | null = Array.isArray(entry.source)
            ? entry.source.map(String)
            : entry.source != null
              ? [String(entry.source)]
              : null;
          _.set(selectionIdsBySection, sectionId, timeLineIds);
        }

        // For ADVBATMGMTRPT report exports that is not in the source
        if (sectionMeta.navigationSourceType === 'ADVBATMGMTRPT') {
          selectedSources.push({
            id: idRef.current++,
            sectionId,
            sectionLabel: '',
            label: t('reports.export.advbatmgmtrpt.exportLabel'),
            nodeId: entry.source,
            hideClose: true,
          });
          const advbatmgmtrptIds: string[] | null = Array.isArray(entry.source)
            ? entry.source.map(String)
            : entry.source != null
              ? [String(entry.source)]
              : null;
          _.set(selectionIdsBySection, sectionId, advbatmgmtrptIds);
        }

        // For SessionSearch report exports that is not in the source
        if (sectionMeta.navigationSourceType === 'SESSIONSEARCH') {
          selectedSources.push({
            id: idRef.current++,
            sectionId,
            sectionLabel: t('reports.export.searchTerm.exportSectionLabel'),
            label: t('reports.export.sessionSearch.exportLabel'),
            nodeId: entry.source,
            hideClose: true,
          });
          const sessionSearchIds: string[] | null = Array.isArray(entry.source)
            ? entry.source.map(String)
            : entry.source != null
              ? [String(entry.source)]
              : null;
          _.set(selectionIdsBySection, sectionId, sessionSearchIds);
        }

        // For Search Term exports that is not in the source
        if (sectionMeta.navigationSourceType === 'SEARCHTERM') {
          selectedSources.push({
            id: idRef.current++,
            sectionId,
            sectionLabel: t('reports.export.searchTerm.exportSectionLabel'),
            label: t('reports.export.searchTerm.exportLabel'),
            nodeId: entry.source,
            hideClose: false,
          });
          const searchTermIds: string[] | null = Array.isArray(entry.source)
            ? entry.source.map(String)
            : entry.source != null
              ? [String(entry.source)]
              : null;
          _.set(selectionIdsBySection, sectionId, searchTermIds);
        }

        if (sectionMeta.navigationSourceType === 'DOCLIST') {
          selectedSources.push({
            id: idRef.current++,
            sectionId,
            sectionLabel: t('docList.exportSectionLabel'),
            label: t('docList.exportLabel'),
            nodeId: entry.source,
            hideClose: false,
          });
          const docListIds: string[] | null = Array.isArray(entry.source)
            ? entry.source.map(String)
            : entry.source != null
              ? [String(entry.source)]
              : null;
          _.set(selectionIdsBySection, sectionId, docListIds);
        }

        // For SavedSearch report exports that is not in the source
        if (sectionMeta.navigationSourceType === 'SAVEDSEARCH') {
          selectedSources.push({
            id: idRef.current++,
            sectionId,
            sectionLabel: '',
            label: t('reports.export.savedSearch.exportLabel'),
            nodeId: entry.source,
            hideClose: true,
          });
          const savedSearchIds: string[] | null = Array.isArray(entry.source)
            ? entry.source.map(String)
            : entry.source != null
              ? [String(entry.source)]
              : null;
          _.set(selectionIdsBySection, sectionId, savedSearchIds);
        }

        // For Assignment report exports that is not in the source
        if (sectionId.toLowerCase() === ASSIGNMENT_EXPORT_SECTION_ID) {
          selectedSources.push({
            id: idRef.current++,
            sectionId,
            sectionLabel: t('reports.export.assignment.exportSectionLabel'),
            label: entry.sourceName ?? '',
            nodeId: entry.source,
            hideClose: true,
          });
          const assignmentIds: string[] | null = Array.isArray(entry.source)
            ? entry.source.map(String)
            : entry.source != null
              ? [String(entry.source)]
              : null;
          _.set(selectionIdsBySection, sectionId, assignmentIds);
          // Show the Assignments source type in the (disabled) dropdown field
          const assignmentsSection = sources.find(
            (s) => s.id === ASSIGNMENT_SOURCE_ID
          );
          setCommittedSourceType({
            id: ASSIGNMENT_SOURCE_ID,
            name:
              assignmentsSection?.label ??
              t('reports.export.assignment.sourceLabel'),
          });
        }

        if (section) {
          const look = buildLookupMap(section?.data ?? []);
          _.set(selectionIdsBySection, sectionId, entry.source);
          (entry?.source ?? []).forEach((id: string) => {
            if (_.get(look, id)) {
              const obj = _.get(look, id);
              const label = pickNodeLabel(obj);
              if (label) {
                selectedSources.push({
                  id: idRef.current++,
                  sectionId,
                  sectionLabel:
                    sectionId === 'INGESTION'
                      ? section.label
                      : sectionMeta.sourceName,
                  label,
                  nodeId: id,
                  hideClose: sectionId === 'INGESTION',
                });
              }
            }
          });
        }
      });
    });
    setSelectedIdsBySection(selectionIdsBySection);
    setFullSelectionsBySection(selectionIdsBySection);
    setDraftSelectedIdsBySection(selectionIdsBySection);
    setSelectedSources(selectedSources);
    publishSelections(buildAggregatedPayload(selectionIdsBySection));
  };

  const getNormalizedId = (n: any): string =>
    String(
      n?.id ??
        n?.keyID ??
        n?.value ??
        n?.securityGroupId ??
        n?.projectId ??
        n?.tagClassificationId ??
        ''
    );

  const buildLookupMap = (flatList: any[] = []): Record<string, any> => {
    const map: Record<string, any> = {};
    (flatList ?? []).forEach((n) => {
      const id = getNormalizedId(n);
      if (id) map[id] = n;
    });
    return map;
  };

  const buildNestedTree = (flatList: any[] = []): any[] => {
    // Use a Map to preserve insertion order from flatList (avoids numeric key ordering surprises).
    const nodeMap: Map<string, any> = new Map();
    const roots: any[] = [];

    (flatList ?? []).forEach((node) => {
      const id = getNormalizedId(node);
      const parentId = node?.parent != null ? String(node.parent) : '';
      nodeMap.set(id, {...node, id, parentId, children: []});
    });

    for (const node of nodeMap.values()) {
      if (node.parentId && nodeMap.has(node.parentId)) {
        const parent = nodeMap.get(node.parentId);
        parent.children.push(node);
      } else {
        roots.push(node);
      }
    }

    return roots;
  };

  const mapToTreeItems = (nodes: any[] = []): any[] =>
    (nodes ?? []).map((n: any) => {
      const id = getNormalizedId(n);
      const label = pickNodeLabel(n);
      const children = Array.isArray(n?.children)
        ? mapToTreeItems(n.children)
        : undefined;
      const docCount =
        typeof n?.documentCount === 'number' && n.documentCount > 0
          ? n.documentCount
          : typeof n?.count === 'number' && n.count > 0
            ? n.count
            : undefined;
      const disableEmptySearchGroups =
        currentSectionId.toLowerCase() === 'searches' &&
        (label === 'Shared With Project Administrator' ||
          label === 'My Saved Search') &&
        (!children || children.length === 0);
      return {
        id,
        label,
        fileType:
          children && children.length
            ? `${t('reports.tally.sourceContainer.folder')}`
            : `${t('reports.tally.sourceContainer.search')}`,
        disabled: !!n?.disabled || disableEmptySearchGroups,
        count: docCount,
        children,
      };
    });

  const currentLookup = useMemo(
    () => buildLookupMap(currentSection?.data ?? []),
    [currentSection]
  );

  const treeItems = useMemo(() => {
    const rawData = sortedSectionData ?? [];
    const nestedData = buildNestedTree(rawData);
    return mapToTreeItems(nestedData);
  }, [sortedSectionData]);

  const cleanNumericIds = (ids: string[] | null | undefined) =>
    (ids ?? []).map(String).filter((v) => /^-?\d+$/.test(v));

  const uniqueIds = (ids: string[] | null) => {
    if (!Array.isArray(ids) || ids.length === 0) return null;
    const seen = new Set<string>();
    const out: string[] = [];
    ids.map(String).forEach((x) => {
      if (!seen.has(x)) {
        seen.add(x);
        out.push(x);
      }
    });
    return out.length ? out : null;
  };

  const handleDraftIdsChangeForSection = (
    sectionId: string,
    ids: string[] | null,
    lookup: Record<string, any>
  ) => {
    setDraftSelectedIdsBySection((prev) => {
      const nextIdsMap = multiSelectSources
        ? {...prev, [sectionId]: ids}
        : {[sectionId]: ids};
      const nextFull = ids ? ids.map((id) => lookup[id]).filter(Boolean) : null;
      setFullSelectionsBySection((prevFull) => {
        const updatedFull = multiSelectSources
          ? {...prevFull, [sectionId]: nextFull}
          : {[sectionId]: nextFull};
        return updatedFull;
      });
      return nextIdsMap;
    });
  };

  const buildAggregatedPayload = (idsMap: Record<string, string[] | null>) => {
    const aggregated: Record<string, any[] | null> = {};
    Object.keys(idsMap).forEach((key) => {
      const meta = getSectionMeta(key);
      const currentIds = cleanNumericIds(idsMap[key]);
      aggregated[key] =
        currentIds && currentIds.length
          ? [
              {
                sourceName: meta.sourceName,
                source: currentIds,
                navigationSourceType: meta.navigationSourceType,
              },
            ]
          : null;
    });
    return aggregated;
  };

  const commitSectionSelection = (
    sectionId: string,
    sectionLabel: string,
    ids: string[] | null,
    lookup: Record<string, any>
  ) => {
    const normalizedIds = uniqueIds(ids);

    if (!normalizedIds || !normalizedIds.length) {
      setDrawerOpen(false);
      setDrawerSourceType(null);
      setDropdownResetKey((k) => k + 1);
      return;
    }

    const groupIdSet = new Set(
      Object.values(lookup)
        .filter((item: any) => item.isGroup)
        .map((item: any) => item.id)
    );

    const entries = normalizedIds
      .filter((id) => !groupIdSet.has(id))
      .map((id) => {
        const obj = lookup[id];
        const label = pickNodeLabel(obj);
        return label ? {label, nodeId: id} : null;
      })
      .filter(Boolean) as {label: string; nodeId: string}[];
    if (!entries.length) {
      setDrawerOpen(false);
      setDrawerSourceType(null);
      setDropdownResetKey((k) => k + 1);
      return;
    }
    setCommittedSourceType(
      drawerSourceType ?? {id: sectionId, name: sectionLabel}
    );
    const entriesByNodeId = new Map(
      entries.map((entry) => [entry.nodeId, entry])
    );
     setSelectedSources((prev) => {
      const filteredPrev = multiSelectSources
        ? prev.filter((p) => p.sectionId !== sectionId)
        : [];
      const next = normalizedIds.flatMap((nodeId) => {
        const entry = entriesByNodeId.get(nodeId);
        return entry
          ? [
              {
                id: idRef.current++,
                sectionId,
                sectionLabel,
                label: entry.label,
                nodeId,
              },
            ]
          : [];
      });
      return [...filteredPrev, ...next];
    });    
    setSelectedIdsBySection((prev) => {
      const map: Record<string, string[] | null> = multiSelectSources
        ? {...prev, [sectionId]: normalizedIds}
        : {[sectionId]: normalizedIds};
      const payload = buildAggregatedPayload(map);
      publishSelections(payload);
      return map;
    });
    setFullSelectionsBySection((prev) => {
      const map: Record<string, any[] | null> = multiSelectSources
        ? {
            ...prev,
            [sectionId]: normalizedIds.map((id) => lookup[id]).filter(Boolean),
          }
        : {[sectionId]: normalizedIds.map((id) => lookup[id]).filter(Boolean)};
      return map;
    });
    setDraftSelectedIdsBySection((prev) => {
      const map: Record<string, string[] | null> = multiSelectSources
        ? {...prev, [sectionId]: normalizedIds}
        : {[sectionId]: normalizedIds};
      return map;
    });
    setDrawerOpen(false);
    setDrawerSourceType(null);
  };

  const handleRemoveChip = (source: SelectedSource) => {
    // when removing a chip, also remove it from selected ids map so that it is reflected in dropdown.
    const newSelectedSources = selectedSources.filter(
      (i) => i.id !== source.id
    );
    setSelectedSources(newSelectedSources);
    if (!newSelectedSources.length) {
      setCommittedSourceType(null);
      setDropdownResetKey((k) => k + 1);
      // Prepopulated source fully removed -> enable the source dropdown
      setDisableSourceSelection(false);
    }
    const current = selectedIdsBySection[source.sectionId] ?? [];
    const nextIds = Array.isArray(current)
      ? current.filter((id) => String(id) !== String(source.nodeId))
      : null;
    const section = sources.find((s) => s.id === source.sectionId);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const lookup = buildLookupMap(section?.data ?? []);
    setSelectedIdsBySection((prev) => {
      const nextMap: Record<string, string[] | null> = multiSelectSources
        ? {
            ...prev,
            [source.sectionId]: nextIds && nextIds.length ? nextIds : null,
          }
        : {[source.sectionId]: nextIds && nextIds.length ? nextIds : null};
      const payload = buildAggregatedPayload(nextMap);
      publishSelections(payload);
      return nextMap;
    });
    setDraftSelectedIdsBySection((prev) => {
      const nextMap: Record<string, string[] | null> = multiSelectSources
        ? {
            ...prev,
            [source.sectionId]: nextIds && nextIds.length ? nextIds : null,
          }
        : {[source.sectionId]: nextIds && nextIds.length ? nextIds : null};
      return nextMap;
    });
    setSelectSourceKey((k) => k + 1);
  };

  const handleOpenDrawerForSourceType = (item: ISelectItem | null) => {
    const selected = sources.find((s) => s.id === item?.id);
    if (selected) {
      setDrawerSourceType(item);
      setDraftSelectedIdsBySection((prev) => {
        const sectionId = item?.id?.toString?.() ?? '';
        const committed = selectedIdsBySection[sectionId] ?? null;
        const map = multiSelectSources
          ? {...prev, [sectionId]: committed}
          : {[sectionId]: committed};
        return map;
      });
      setDrawerOpen(true);
      setSelectSourceKey((k) => k + 1);
      setDropdownResetKey((k) => k + 1);
    }
  };

  const handleDrawerClose = () => {
    if (currentSectionId) {
      setDraftSelectedIdsBySection((prev) => {
        const committed = selectedIdsBySection[currentSectionId] ?? null;
        const map = multiSelectSources
          ? {...prev, [currentSectionId]: committed}
          : {[currentSectionId]: committed};
        return map;
      });
    }
    setDrawerOpen(false);
    setDrawerSourceType(null);
    setSelectSourceKey((k) => k + 1);
  };

  useEffect(() => {
    const initial = initialSelectedSources as Record<string, any> | undefined;
    if (!initial || !sources.length) return;
    if (selectedSources.length > 0) return;
    if (Object.keys(selectedIdsBySection).length > 0) return;

    const sectionIds = Object.keys(initial).filter(Boolean);
    if (!sectionIds.length) return;

    const nextSelectedIdsBySection: Record<string, string[] | null> = {};
    const nextDraftSelectedIdsBySection: Record<string, string[] | null> = {};
    const nextFull: Record<string, any[] | null> = {};
    const nextChips: SelectedSource[] = [];

    sectionIds.forEach((sectionId) => {
      const rawIds = initial[sectionId];
      const ids = Array.isArray(rawIds)
        ? rawIds.map(String)
        : rawIds != null
          ? [String(rawIds)]
          : [];
      const deduped = uniqueIds(ids);
      nextSelectedIdsBySection[sectionId] = deduped;
      nextDraftSelectedIdsBySection[sectionId] = deduped;

      const section = sources.find((s) => s.id === sectionId);
      const lookup = buildLookupMap(section?.data ?? []);
      const sectionLabel = section?.label ?? sectionId;

      const full = deduped
        ? deduped.map((id) => lookup[id]).filter(Boolean)
        : null;
      nextFull[sectionId] = full;

      (deduped ?? []).forEach((id) => {
        const obj = lookup[id];
        const label = pickNodeLabel(obj);
        if (!label) return;
        nextChips.push({
          id: idRef.current++,
          sectionId,
          sectionLabel,
          label,
          nodeId: id,
        });
      });
    });

    if (!nextChips.length) return;

    const firstSectionId = sectionIds[0];
    const firstSection = sources.find((s) => s.id === firstSectionId);
    if (firstSection) {
      setCommittedSourceType({id: firstSection.id, name: firstSection.label});
    }

    setSelectedSources(nextChips);
    setSelectedIdsBySection(nextSelectedIdsBySection);
    setDraftSelectedIdsBySection(nextDraftSelectedIdsBySection);
    setFullSelectionsBySection(nextFull);
    publishSelections(buildAggregatedPayload(nextSelectedIdsBySection));
    setDropdownResetKey((k) => k + 1);
  }, [initialSelectedSources, sources]);

  const trimPluralLastWord = (text: any) => {
    const trimmed = text.trimEnd();
    const parts = trimmed.split(' ');
    const lastWord = parts.pop();
    const lower = lastWord.toLowerCase();

    let updatedWord = lastWord;

    if (!/(ss|us)$/.test(lower)) {
      if (/(ches|shes|xes|zes|ses)$/.test(lower)) {
        updatedWord = lastWord.slice(0, -2);
      } else if (/s$/.test(lower)) {
        updatedWord = lastWord.slice(0, -1);
      }
    }

    return [...parts, updatedWord].join(' ');
  };

  const headerTitle = (
    <Box className="flexCenter">
      <Box className="flexBoxWhite">
        <Typography
          variant="caption"
          className="captionBlock"
          data-testid={`arr-source-selected-section-source`}
        >
          {t('ingestions.mapped.tileMenu.source')}:
        </Typography>
        <Typography
          variant="h6"
          className="typographyEllipsis"
          data-testid={`arr-source-selected-section-label`}
        >
          {currentSection?.label}
        </Typography>
      </Box>
      <Box className="button-container">
        <Button
          variant="contained"
          className="cancel-btn"
          onClick={handleDrawerClose}
          data-testid={`arr-cancel-button-${currentSectionId}`}
        >
          <Typography
            className="cancel-button-text"
            data-testid={`arr-source-selected-section-cancel-${currentSectionId}`}
          >
            {t('common.cancel')}
          </Typography>
        </Button>
        <Button
          variant="contained"
          className={`select-btn ${
            !Array.isArray(draftSelectedIdsBySection[currentSectionId]) ||
            draftSelectedIdsBySection[currentSectionId]?.length === 0
              ? 'inactive'
              : ''
          }`}
          onClick={() =>
            commitSectionSelection(
              currentSectionId,
              currentSection?.label ?? t('ingestions.mapped.tileMenu.source'),
              Array.isArray(draftSelectedIdsBySection[currentSectionId])
                ? draftSelectedIdsBySection[currentSectionId]
                : null,
              currentLookup
            )
          }
          disabled={
            !Array.isArray(draftSelectedIdsBySection[currentSectionId]) ||
            draftSelectedIdsBySection[currentSectionId]?.length === 0
          }
          data-testid={`arr-save-button-${currentSectionId}`}
        >
          <Typography
            className={`select-button-text ${
              !Array.isArray(draftSelectedIdsBySection[currentSectionId]) ||
              draftSelectedIdsBySection[currentSectionId]?.length === 0
                ? 'inactive'
                : ''
            }`}
            data-testid={`arr-source-selected-section-select-${currentSectionId}`}
          >
            {t('common.select')}
          </Typography>
        </Button>
      </Box>
    </Box>
  );

  const renderDrawer = (isOpen: boolean, onClose: () => void): JSX.Element => (
    <ArrDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={headerTitle as unknown as string}
      showButton={false}
      size="small"
      side="right"
      data-testid="arr-source-drawer"
    >
      <Box className="drawerContent" data-testid="arr-drawer-content">
        <Box
          className="arr-tree-container"
          data-testid={`arr-tree-container-${currentSectionId}`}
        >
          <ArrTreeView
            key={selectSourceKey}
            itemsList={treeItems}
            isShowCheckbox={true}
            multipleSelection={true}
            treeSelectedItems={
              Array.isArray(draftSelectedIdsBySection[currentSectionId])
                ? draftSelectedIdsBySection[currentSectionId]
                : null
            }
            setSelectedItem={(val: any) => {
              const ids = Array.isArray(val)
                ? val.map(String)
                : val
                  ? [String(val)]
                  : [];
              handleDraftIdsChangeForSection(
                currentSectionId,
                ids.length ? ids : null,
                currentLookup
              );
            }}
            defaultExpandedIds={[]}
            data-testid={`arr-treeview-${currentSectionId}`}
            isShowFileType={false}
            isChildrenIndendation={true}
            groupFoldersFirst={true}
          />
        </Box>
      </Box>
    </ArrDrawer>
  );

  return (
    <>
      <ArrSingleSelectDropdown
        key={`arr-source-type-dropdown-${dropdownResetKey}`}
        dropdownLabel={t('reports.tally.sourceContainer.selectSource')}
        dropdownItems={sourceOptions}
        selectedValues={committedSourceType}
        onSelectionChange={(item: ISelectItem | null) => {
          handleOpenDrawerForSourceType(item);
        }}
        sizeVariant={sourcesDropdownSize}
        customWidth={16.3}
        dataTestId="asd-select-source-arr-source-type-dropdown"
        disabled={disabledSourceSelection}
      />
      {selectedSources.length > 0 ? (
        <Box className="results-box">
          <Box
            className="selected-header"
            style={{display: 'flex', alignItems: 'center'}}
          >
            <Typography
              className="result-count typo-text body4_size"
              data-testid={`arr-source-selected-section-selected-length`}
            >
              {`Selected (${selectedSources.length})`}
            </Typography>

            <Button
              className="show-hide-button"
              size="small"
              disableRipple
              disableFocusRipple
              disableElevation
              onClick={() => setIsExpanded((prev) => !prev)}
              endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              data-testid="arr-toggle-selected"
              sx={{
                textTransform: 'none',
              }}
            >
              {isExpanded ? 'Hide' : 'Show'}
            </Button>
          </Box>
          <Collapse in={isExpanded}>
            <Box
              className="arr-chips-container"
              data-testid="arr-chips-container"
            >
              {selectedSources.map((source) => (
                <ArrSelectionChip
                  key={`${source.sectionId}:${source.id}`}
                  filterData={{
                    id: `${source.sectionId}:${source.id}`,
                    label: `${trimPluralLastWord(source.sectionLabel)}${
                      source.sectionLabel === '' ? '' : ':'
                    } ${source.label}`,
                    isSelected: false,
                  }}
                  onClick={() => handleRemoveChip(source)}
                  data-testid={`arr-chip-${source.sectionId}-${source.id}`}
                  variant="v2"
                  showCloseIcon={true}
                  noIcon={source.hideClose ?? false}
                  labelTextTestId={source.sectionId}
                />
              ))}
            </Box>
          </Collapse>
        </Box>
      ) : null}
      {drawerOpen && renderDrawer(drawerOpen, handleDrawerClose)}
    </>
  );
};

export default ArrSourceSelectedSection;
